// Consultas a la API desde las pantallas: carga, error con reintento y una caché en memoria para que volver a una
// pantalla muestre lo último al instante mientras se trae lo nuevo.
import { useCallback, useEffect, useRef, useState } from 'react';
import { mensajeDeError, SinRed } from './api';

const cache = new Map<string, unknown>();

export const olvidarConsultas = (prefijo = ''): void => {
  for (const k of [...cache.keys()]) if (k.startsWith(prefijo)) cache.delete(k);
};

export const leerCache = <T>(clave: string): T | undefined => cache.get(clave) as T | undefined;
export const escribirCache = (clave: string, valor: unknown): void => {
  cache.set(clave, valor);
};

export interface Consulta<T> {
  datos: T | undefined;
  /** Primera carga sin nada para mostrar (para los esqueletos). */
  cargando: boolean;
  error: string | null;
  sinRed: boolean;
  recargar: () => Promise<void>;
  fijar: (d: T) => void;
}

/**
 * `clave` null no consulta (falta el negocio, por ejemplo). Con `cadaMs` se vuelve a consultar sola mientras la
 * pantalla está a la vista (el seguimiento del pedido).
 */
export function useConsulta<T>(
  clave: string | null,
  f: () => Promise<T>,
  opciones: { cadaMs?: number } = {},
): Consulta<T> {
  const [datos, setDatos] = useState<T | undefined>(() => (clave ? leerCache<T>(clave) : undefined));
  const [error, setError] = useState<string | null>(null);
  const [sinRed, setSinRed] = useState(false);
  const [cargando, setCargando] = useState(() => !!clave && !cache.has(clave));
  const fRef = useRef(f);
  fRef.current = f;
  const claveRef = useRef(clave);
  claveRef.current = clave;

  const recargar = useCallback(async () => {
    const k = claveRef.current;
    if (!k) return;
    if (!cache.has(k)) setCargando(true);
    try {
      const d = await fRef.current();
      if (claveRef.current !== k) return;
      cache.set(k, d);
      setDatos(d);
      setError(null);
      setSinRed(false);
    } catch (e) {
      if (claveRef.current !== k) return;
      setError(mensajeDeError(e));
      setSinRed(e instanceof SinRed);
    } finally {
      if (claveRef.current === k) setCargando(false);
    }
  }, []);

  useEffect(() => {
    setDatos(clave ? leerCache<T>(clave) : undefined);
    setError(null);
    setCargando(!!clave && !cache.has(clave));
    void recargar();
  }, [clave, recargar]);

  const cada = opciones.cadaMs;
  useEffect(() => {
    if (!cada || !clave) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void recargar();
    }, cada);
    const alVolver = () => document.visibilityState === 'visible' && void recargar();
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [cada, clave, recargar]);

  const fijar = useCallback((d: T) => {
    if (claveRef.current) cache.set(claveRef.current, d);
    setDatos(d);
  }, []);

  return { datos, cargando: cargando && datos === undefined, error, sinRed, recargar, fijar };
}

/** Espera corta al tipear: busca cuando deja de escribir. */
export function useDemorado<T>(valor: T, ms = 350): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

/** Hay señal (según el navegador; la confirmación real es que la API responda). */
export function useEnLinea(): boolean {
  const [enLinea, setEnLinea] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const si = () => setEnLinea(true);
    const no = () => setEnLinea(false);
    window.addEventListener('online', si);
    window.addEventListener('offline', no);
    return () => {
      window.removeEventListener('online', si);
      window.removeEventListener('offline', no);
    };
  }, []);
  return enLinea;
}
